// The pinned beta's own measurement tools, with IMEXHS labels and units.
// Their text is built inside private closures, so each subclass re-labels what the parent draws
// (withTranslatedText) and records the workspace readout from the values the parent stores on the data.
import { builtInTool, csCore, csTools, type AnnotationToolClass, type MeasurementData, type Point, type Readout, type RenderDetail, type ToolImage } from './cstypes';
import { canvasDistanceToSegment, formatAngle, formatLength, modalityOf, spacingOf, type Spacing, withTranslatedText } from './draw';
import { currentMoveDistance } from './proximity';
import { type HandleMap, measureCobb, round } from './geometry';

const SQUARED_SUFFIX = { mm: 'mm²', pixels: 'pixels^2' } as const;
const HU = 'HU';

const PROBE_DECIMALS = 3;
const DEGREES = '°';
const ISOTROPIC_SUFFIX = ' (isotropic)';

type Translator = (text: string, image: ToolImage) => string;
type ReadoutBuilder = (data: MeasurementData, image: ToolImage, element: HTMLElement) => Readout[];

interface Localization {
  className: string;
  translate: Translator;
  readout: ReadoutBuilder;
  /** Runs before the beta draws (values the beta would otherwise only compute on modification). */
  prepare?(data: MeasurementData, image: ToolImage): void;
}

/** Pixel value under a probe, cached on the measurement for the workspace and the wire format. */
export interface ProbeValue {
  /** Modality value (HU on CT), 3 decimals; undefined on colour images. */
  value?: number;
  /** SUV (g/ml) on PT when the beta can compute it. */
  suv?: number;
  /** "R G B" on colour images. */
  rgb?: string;
}

function isCt(image: ToolImage): boolean {
  return modalityOf(image.imageId) === 'CT';
}

function areaText(area: number, spacing: Spacing): string {
  return `${area.toFixed(2)} ${spacing.calibrated ? SQUARED_SUFFIX.mm : SQUARED_SUFFIX.pixels}`;
}

/** "Mean:/StdDev:/Area:" lines of the beta ROI tools → "Media:/DE:/Área:", squared units per IMEXHS. */
const translateRoi: Translator = (text) =>
  text.replace(/^Mean: /, 'Media: ').replace(/^StdDev: /, 'DE: ').replace(/^Area: /, 'Área: ').replace(/ pixels²$/, ` ${SQUARED_SUFFIX.pixels}`);

function roiReadout(data: MeasurementData, image: ToolImage): Readout[] {
  const lines: Readout[] = [];
  const area = typeof data.area === 'number' ? data.area : undefined;
  if (area !== undefined) lines.push({ label: 'Área:', value: areaText(area, spacingOf(image)) });
  const stats = data.meanStdDev as { mean?: number; stdDev?: number } | undefined;
  const unit = isCt(image) ? ` ${HU}` : '';
  if (stats?.mean !== undefined) lines.push({ label: 'Media:', value: `${stats.mean.toFixed(2)}${unit}` });
  if (stats?.stdDev !== undefined) lines.push({ label: 'DE:', value: `${stats.stdDev.toFixed(2)}${unit}` });
  return lines;
}

function lengthReadout(data: MeasurementData, image: ToolImage): Readout[] {
  return typeof data.length === 'number' ? [{ label: 'Longitud:', value: formatLength(data.length, spacingOf(image)) }] : [];
}

function angleReadout(data: MeasurementData): Readout[] {
  return typeof data.rAngle === 'number' && Number.isFinite(data.rAngle) ? [{ label: 'Angulo:', value: formatAngle(data.rAngle) }] : [];
}

function cobbComplete(data: MeasurementData): boolean {
  const { start, end, start2, end2 } = data.handles;
  return data.complete === true && start !== undefined && end !== undefined && start2 !== undefined && end2 !== undefined;
}

/** Cobb angle recomputed from the handles on every render (the beta only does it on modification). */
function prepareCobb(data: MeasurementData, image: ToolImage): void {
  if (!cobbComplete(data)) return;
  const spacing = spacingOf(image);
  const angle = round(measureCobb(data.handles as HandleMap, spacing), 2);
  data.value = `${angle}${DEGREES}${spacing.calibrated ? '' : ISOTROPIC_SUFFIX}`;
}

function cobbReadout(data: MeasurementData, image: ToolImage): Readout[] {
  if (!cobbComplete(data)) return [];
  return [{ label: 'Angulo Cobb:', value: formatAngle(measureCobb(data.handles as HandleMap, spacingOf(image))) }];
}

/** Probe: modality value (HU on CT) for grayscale images, RGB for colour ones; cached on the data. */
function probeReadout(data: MeasurementData, image: ToolImage, element: HTMLElement): Readout[] {
  const end = data.handles.end as Point | undefined;
  data.probe = undefined;
  if (!end) return [];
  const x = Math.round(end.x);
  const y = Math.round(end.y);
  if (x < 0 || y < 0 || x >= image.columns || y >= image.rows) return [];
  if (image.color) {
    const [r, g, b] = csTools().getRGBPixels(element, x, y, 1, 1);
    data.probe = { rgb: `${r} ${g} ${b}` } satisfies ProbeValue;
    return [{ label: 'Intensidad Pixel:', value: `R: ${r} G: ${g} B: ${b}` }];
  }
  const stored = csCore().getStoredPixels(element, x, y, 1, 1)[0];
  const value = parseFloat((stored * image.slope + image.intercept).toFixed(PROBE_DECIMALS));
  const modality = modalityOf(image.imageId);
  const suv = modality === 'PT' ? csTools().calculateSUV?.(image, stored) : undefined;
  const probe: ProbeValue = { value };
  if (typeof suv === 'number' && Number.isFinite(suv)) probe.suv = parseFloat(suv.toFixed(PROBE_DECIMALS));
  data.probe = probe;
  return [{ label: 'D:', value: isCt(image) ? `${value} ${HU}` : String(value) }];
}

const LOCALIZATIONS: Record<string, Localization> = {
  Length: {
    className: 'LengthTool',
    translate: (text) => text.replace(/^(-?[\d.]+) (mm|pixels)$/, 'Longitud: $1 $2'),
    readout: lengthReadout,
  },
  Angle: {
    className: 'AngleTool',
    translate: (text) => text.replace(/^(-?[\d.]+)°( \(isotropic\))?$/, 'Angulo: $1°'),
    readout: angleReadout,
  },
  CobbAngle: {
    className: 'CobbAngleTool',
    translate: (text) => text.replace(/^(-?[\d.]+)°( \(isotropic\))?$/, 'Angulo Cobb: $1°'),
    readout: cobbReadout,
    prepare: prepareCobb,
  },
  Probe: {
    className: 'ProbeTool',
    translate: (text, image) =>
      text.replace(/^SP: -?[\d.]+ MO: (-?[\d.e+-]+)(.*)$/, (_match, mo: string, rest: string) => `D: ${mo}${isCt(image) ? ` ${HU}` : ''}${rest}`),
    readout: probeReadout,
  },
  EllipticalRoi: { className: 'EllipticalRoiTool', translate: translateRoi, readout: roiReadout },
  RectangleRoi: { className: 'RectangleRoiTool', translate: translateRoi, readout: roiReadout },
  FreehandMouse: { className: 'FreehandMouseTool', translate: translateRoi, readout: roiReadout },
};

/** Handle pairs whose segments make up each line tool (what a press must be near to move it). */
const LINE_SEGMENTS: Record<string, Array<[string, string]>> = {
  Length: [['start', 'end']],
  Angle: [['start', 'middle'], ['middle', 'end']],
  CobbAngle: [['start', 'end'], ['start2', 'end2']],
};

/** Subclass of a beta tool that draws Spanish labels and records the workspace readout. */
function localize(name: string, spec: Localization): AnnotationToolClass {
  const Parent = builtInTool(spec.className);
  return class LocalizedTool extends Parent {
    constructor(toolName: string = name) {
      super(toolName);
    }

    /**
     * Loaded read-only measurements are drawn but never picked. Line tools are picked within the
     * configured move distance (tools/proximity.ts) instead of the beta's fixed 25 px.
     */
    pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean {
      if (data.readOnly === true) return false;
      const segments = LINE_SEGMENTS[name];
      if (!segments) return super.pointNearTool(element, data, coords);
      if (data.visible === false) return false;
      const limit = currentMoveDistance();
      return segments.some(([from, to]) => {
        const a = data.handles[from];
        const b = data.handles[to];
        return !!a && !!b && canvasDistanceToSegment(element, a, b, coords) < limit;
      });
    }

    renderToolData(evt: CustomEvent<RenderDetail>): void {
      const { element, image, canvasContext } = evt.detail;
      if (spec.prepare) {
        const pending = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
        pending?.forEach((data) => spec.prepare?.(data, image));
      }
      withTranslatedText(canvasContext, (text) => spec.translate(text, image), () => super.renderToolData(evt));
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      toolData?.forEach((data) => {
        data.readout = spec.readout(data, image, element);
      });
    }
  };
}

/** Beta tool name → localized class (tool names stay the beta defaults). */
export function localizedTools(): Record<string, AnnotationToolClass> {
  return Object.fromEntries(Object.entries(LOCALIZATIONS).map(([name, spec]) => [name, localize(name, spec)]));
}
